import { useState } from 'react';
import { Button } from './Button';
import { cancelBooking, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { PS, T } from '../lib/theme';
import { nextFreeLine, occupancyLine } from '../lib/occupancy';
import type { SeriesConflictItem, SeriesConflictsDetail } from '../lib/types';

interface Props {
  detail: SeriesConflictsDetail;
  onResolved: () => void;
}

/**
 * The 409 from `POST /admin/coach-series`, rendered.
 *
 * Unlike `SlotConflictBanner` this one is actionable: a series projects over
 * dozens of dates, so resolving in place is the only workable flow, whereas a
 * single session can simply be moved. See
 * .ai/features/47-one-time-coach-booking/DECISIONS.md.
 *
 * Entries are canonical occupancy records since givip/prospin-api#121, so they
 * may be coach blocks or unpaid holds, not only confirmed bookings — a hold has
 * no booking row and therefore nothing to cancel.
 *
 * The one place this deliberately departs from `SlotConflictBanner`: that banner
 * renders `detail.error` verbatim, this one replaces it with a count. The API's
 * sentence ("Series projection conflicts with existing bookings") says less than
 * "conflicts with 3 occupied slots" once a projection can return several.
 */
export function SeriesConflictsBanner({ detail, onResolved }: Props) {
  const { push } = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);

  const conflicts = detail.conflicts ?? [];

  const handleCancel = async (item: SeriesConflictItem) => {
    if (item.booking_id == null) return;
    setBusyId(item.booking_id);
    try {
      await cancelBooking(item.booking_id);
      push('success', `Booking #${item.booking_id} cancelled.`);
      onResolved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Cancel failed';
      push('error', msg);
    } finally {
      setBusyId(null);
    }
  };

  if (conflicts.length === 0) return null;

  // `next_free_time` applies to the earliest conflicting date only, so name that
  // date — without it the time reads as if it held for the whole projection.
  // Computed rather than read off `conflicts[0]`: the API does build the list in
  // projected-date order, but that is an implementation detail of its loop, and
  // this sentence should not rest on it. ISO dates make lexicographic min
  // chronological min. Non-null by construction — the length guard is above.
  const firstDate = conflicts.reduce((min, c) => (c.date < min ? c.date : min), conflicts[0].date);
  const nextFree = nextFreeLine(detail.next_free_time, firstDate);

  return (
    <div
      style={{
        ...T.caption,
        padding: 12,
        background: PS.errorBg,
        border: `1px solid ${PS.errorBorder}`,
        borderRadius: 3,
        color: PS.errorText,
      }}
    >
      <div style={{ ...T.subheading, color: PS.errorText, marginBottom: 8 }}>
        {/* "Occupied slot", not "booking": the list may hold coach blocks and
            unpaid holds too. Counts occupancies, not dates — one date can
            contribute several. */}
        Series conflicts with {conflicts.length} occupied slot
        {conflicts.length === 1 ? '' : 's'}
      </div>
      <div style={{ ...T.caption, color: PS.errorText, marginBottom: 8 }}>
        Resolve the conflicts below, then retry creating the series. Only confirmed bookings can be
        cancelled here.
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {conflicts.map((c, i) => (
          <li
            // The index fallback is namespaced (`idx…`) so it cannot collide
            // with a real `booking_id`: a hold at index 1 and booking #1 on the
            // same date and start time would otherwise produce the same key.
            // `kind` is in there because two occupancies can share a window.
            key={`${c.date}-${c.start_time}-${c.kind}-${c.booking_id ?? `idx${i}`}`}
            style={{ marginBottom: 6 }}
          >
            <span>
              {c.date} · {occupancyLine(c)}
            </span>
            {c.booking_id != null && (
              <span style={{ marginLeft: 8 }}>
                <Button
                  variant="danger"
                  onClick={() => void handleCancel(c)}
                  disabled={busyId === c.booking_id}
                >
                  {busyId === c.booking_id ? 'Cancelling…' : 'Cancel this'}
                </Button>
              </span>
            )}
          </li>
        ))}
      </ul>

      {nextFree && (
        <div style={{ ...T.caption, color: PS.errorText, marginTop: 8 }}>{nextFree}</div>
      )}
    </div>
  );
}
